import CloseIcon from "@mui/icons-material/Close";
import { Divider, IconButton } from "@mui/material";
import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { GetTabHistory } from "Services/MicrobiologyResultData";
import useLang from "Shared/hooks/useLanguage";

interface ViewHistoricalDataModalProps {
  show: boolean;
  onHide: () => void;
  reqOrderId?: number;
  tabId?: number;
}

const ViewHistoricalDataModal: React.FC<ViewHistoricalDataModalProps> = ({ show, onHide, reqOrderId, tabId }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [historicalData, setHistoricalData] = useState<any>(null);

  useEffect(() => {
    if (show && reqOrderId && tabId !== undefined) {
      fetchHistoricalData();
    } else if (show && (!reqOrderId || tabId === undefined)) {
      setHistoricalData(null);
    }
  }, [show, reqOrderId, tabId]);

  const fetchHistoricalData = async () => {
    if (!reqOrderId || tabId === undefined) {
      toast.error(t("Missing required data for historical data"));
      return;
    }

    setLoading(true);
    try {
      const res: AxiosResponse = await GetTabHistory({
        reqOrderId: reqOrderId,
        tabId: tabId,
      });

      if (res?.data?.statusCode === 200) {
        setHistoricalData(res?.data?.data || res?.data);
      } else {
        toast.error(t(res?.data?.message || "Failed to load historical data"));
        setHistoricalData(null);
      }
    } catch (err: any) {
      console.error("Error fetching historical data:", err);
      toast.error(t(err?.response?.data?.message || "Error loading historical data"));
      setHistoricalData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="py-4 d-flex justify-content-between align-items-center">
        <Modal.Title className="h5 fw-bold">View Historical Data</Modal.Title>
        <IconButton
          onClick={onHide}
          aria-label="close"
          size="small"
          sx={{ ml: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </Modal.Header>
      <Divider />
      <Modal.Body className="py-4 px-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("Loading...")}</span>
            </div>
          </div>
        ) : historicalData ? (
          <>
            {typeof historicalData === "object" &&
            historicalData !== null &&
            !Array.isArray(historicalData) &&
            Object.keys(historicalData).length > 0 ? (
              Object.entries(historicalData).map(([sectionKey, sectionValue]) => (
                <div key={sectionKey} className="mb-4">
                  <div className="mb-2">
                    <h6 className="text-primary h6 mb-0">{t(sectionKey)}</h6>
                  </div>
                  <div className="table_bordered overflow-hidden">
                    <table className="table table-bordered table-head-2-bg table-bg table-head-custom mb-0">
                      <tbody>
                        {sectionValue !== null &&
                        sectionValue !== undefined &&
                        typeof sectionValue === "object" &&
                        !Array.isArray(sectionValue) ? (
                          Object.entries(sectionValue).map(([rowKey, rowVal]) => (
                            <tr key={rowKey}>
                              <td className="fw-bold text-dark text-start py-2">{t(rowKey)}</td>
                              <td className="text-dark text-start py-2">
                                {rowVal !== null && rowVal !== undefined ? String(rowVal) : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="fw-bold text-dark text-start py-2">{t(sectionKey)}</td>
                            <td className="text-dark text-start py-2">
                              {sectionValue !== null && sectionValue !== undefined
                                ? String(sectionValue)
                                : "-"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p>{t("No historical data available")}</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p>{t("No historical data available")}</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2 border-0">
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={onHide}
        >
          {t("Cancel")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewHistoricalDataModal;
